import { Calendar, Truck, Filter, SortAsc, Package, ChevronLeft, ChevronRight } from "lucide-react";
import { FC, useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { Select, Button, Card, Tag, Calendar as AntCalendar } from "antd";
import { SortBy, useVesselLoadListStore } from "../../operations/vessels-shuttles/store/vesselLoadList.store";
import { ServiceType } from "~/utils/constants";
import { useGetVessels } from "~/services/vessel";
import { useGetServiceTickets } from "~/services/service-ticket";

type Props = {};

export const FilterLoadLists: FC<Props> = () => {
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());

  const { setFilters, setSortBy, filters } = useVesselLoadListStore();
  const { data: vessels } = useGetVessels({ getAll: true });

  // Get service tickets for the selected date to show counts (deliveries + warehouse releases)
  const { data: serviceTickets } = useGetServiceTickets({
    startDate: selectedDate.startOf("day").toISOString(),
    endDate: selectedDate.endOf("day").toISOString(),
    include: 'inventories', // Include inventories for counting
  });

  // Get service tickets for the entire month for calendar shading (deliveries + warehouse releases)
  const { data: monthServiceTickets } = useGetServiceTickets({
    startDate: selectedDate.startOf("month").toISOString(),
    endDate: selectedDate.endOf("month").toISOString(),
    include: 'vessel',
  });

  // Get vessels with service tickets for the selected date
  const vesselsWithServices = vessels?.items?.map(vessel => {
    if (!serviceTickets?.items) return { vessel, serviceCount: 0, inventoryCount: 0 };

    const vesselTickets = serviceTickets.items.filter(ticket => ticket.vessel?.id === vessel.id);
    const serviceCount = vesselTickets.length;
    const inventoryCount = vesselTickets.reduce((total, ticket) => {
      return total + (ticket.inventories?.filter(inv =>
        !inv.status || inv.status === 'available' || inv.status === 'availableonhand' || inv.status === ''
      ).length || 0);
    }, 0);

    return { vessel, serviceCount, inventoryCount };
  }).filter(v => v.serviceCount > 0) || [];

  // Get vessel count per day for calendar shading
  const getVesselCountForDate = (date: Dayjs): number => {
    if (!monthServiceTickets?.items) return 0;

    const dateStr = date.format('YYYY-MM-DD');
    const uniqueVessels = new Set<string>();

    monthServiceTickets.items.forEach(ticket => {
      // Use serviceDatetime if available, otherwise fall back to startDatetime
      const dateToUse = ticket.serviceDatetime || ticket.startDatetime;
      const ticketDate = dayjs(dateToUse).format('YYYY-MM-DD');
      if (ticketDate === dateStr && ticket.vessel?.id && ticket.serviceType === ServiceType.DELIVERY) {
        uniqueVessels.add(ticket.vessel.id);
      }
    });

    return uniqueVessels.size;
  };

  // Get warehouse release count per day for calendar
  const getWarehouseReleaseCountForDate = (date: Dayjs): number => {
    if (!monthServiceTickets?.items) return 0;

    const dateStr = date.format('YYYY-MM-DD');
    let count = 0;

    monthServiceTickets.items.forEach(ticket => {
      const dateToUse = ticket.serviceDatetime || ticket.startDatetime;
      const ticketDate = dayjs(dateToUse).format('YYYY-MM-DD');

      // Count warehouse services with RELEASE add-on
      if (ticketDate === dateStr &&
          ticket.serviceType === ServiceType.WAREHOUSE_SERVICE &&
          ticket.addOnServices?.some((addOn: any) =>
            addOn.name?.toLowerCase().includes('release')
          )) {
        count++;
      }
    });

    return count;
  };

  // Get dates for 2-week view centered on selected date
  const getTwoWeekRange = () => {
    const startOfWeek = selectedDate.startOf('week');
    const dates: Dayjs[] = [];

    // Get current week and next week (14 days)
    for (let i = 0; i < 14; i++) {
      dates.push(startOfWeek.add(i, 'day'));
    }

    return dates;
  };

  const twoWeekDates = getTwoWeekRange();

  const handlePrevWeek = () => {
    setSelectedDate(selectedDate.subtract(1, 'week'));
  };

  const handleNextWeek = () => {
    setSelectedDate(selectedDate.add(1, 'week'));
  };

  useEffect(() => {
    let filters: any = {};

    // Load lists are specifically for DELIVERY services (inventory being loaded for delivery)
    filters.serviceType = ServiceType.DELIVERY;

    // Add date filter for the selected date
    if (selectedDate) {
      filters.startDate = selectedDate.startOf("day").toISOString();
      filters.endDate = selectedDate.endOf("day").toISOString();
    }

    setFilters(filters);
  }, [selectedDate, setFilters]);

  const clearFilters = () => {
    setSelectedDate(dayjs());
  };

  return (
    <div className="mb-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left side - 2 Week Calendar */}
        <div className="lg:col-span-1">
          <Card>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <Calendar size={18} className="text-blue-600" />
                <h3 className="text-base font-semibold">Select Date</h3>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  size="small"
                  icon={<ChevronLeft size={14} />}
                  onClick={handlePrevWeek}
                />
                <Button
                  size="small"
                  icon={<ChevronRight size={14} />}
                  onClick={handleNextWeek}
                />
              </div>
            </div>

            {/* 2 Week Grid View */}
            <div className="space-y-1">
              {/* Week Headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                  <div key={day} className="text-center text-xs font-medium text-gray-600 py-1">
                    {day}
                  </div>
                ))}
              </div>

              {/* Week 1 */}
              <div className="grid grid-cols-7 gap-1">
                {twoWeekDates.slice(0, 7).map((date) => {
                  const vesselCount = getVesselCountForDate(date);
                  const warehouseCount = getWarehouseReleaseCountForDate(date);
                  const isSelected = date.isSame(selectedDate, 'day');
                  const isToday = date.isSame(dayjs(), 'day');

                  let bgColor = 'bg-white hover:bg-gray-50';
                  if (vesselCount >= 5) {
                    bgColor = 'bg-red-50 hover:bg-red-100';
                  } else if (vesselCount >= 3) {
                    bgColor = 'bg-orange-50 hover:bg-orange-100';
                  } else if (vesselCount >= 1) {
                    bgColor = 'bg-yellow-50 hover:bg-yellow-100';
                  }

                  return (
                    <div
                      key={date.format('YYYY-MM-DD')}
                      onClick={() => setSelectedDate(date)}
                      className={`
                        ${bgColor}
                        ${isSelected ? 'ring-2 ring-blue-500' : 'border'}
                        ${isToday ? 'border-blue-400 border-2' : 'border-gray-200'}
                        cursor-pointer rounded p-1 text-center
                      `}
                    >
                      <div className={`text-sm ${isToday ? 'font-bold text-blue-600' : 'text-gray-700'}`}>
                        {date.date()}
                      </div>
                      <div className="flex flex-col gap-0.5 mt-0.5">
                        {vesselCount > 0 && (
                          <div className="text-xs text-blue-600 font-medium">
                            {vesselCount}v
                          </div>
                        )}
                        {warehouseCount > 0 && (
                          <div className="text-xs text-green-600 font-medium">
                            {warehouseCount}w
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Week 2 */}
              <div className="grid grid-cols-7 gap-1">
                {twoWeekDates.slice(7, 14).map((date) => {
                  const vesselCount = getVesselCountForDate(date);
                  const warehouseCount = getWarehouseReleaseCountForDate(date);
                  const isSelected = date.isSame(selectedDate, 'day');
                  const isToday = date.isSame(dayjs(), 'day');

                  let bgColor = 'bg-white hover:bg-gray-50';
                  if (vesselCount >= 5) {
                    bgColor = 'bg-red-50 hover:bg-red-100';
                  } else if (vesselCount >= 3) {
                    bgColor = 'bg-orange-50 hover:bg-orange-100';
                  } else if (vesselCount >= 1) {
                    bgColor = 'bg-yellow-50 hover:bg-yellow-100';
                  }

                  return (
                    <div
                      key={date.format('YYYY-MM-DD')}
                      onClick={() => setSelectedDate(date)}
                      className={`
                        ${bgColor}
                        ${isSelected ? 'ring-2 ring-blue-500' : 'border'}
                        ${isToday ? 'border-blue-400 border-2' : 'border-gray-200'}
                        cursor-pointer rounded p-1 text-center
                      `}
                    >
                      <div className={`text-sm ${isToday ? 'font-bold text-blue-600' : 'text-gray-700'}`}>
                        {date.date()}
                      </div>
                      <div className="flex flex-col gap-0.5 mt-0.5">
                        {vesselCount > 0 && (
                          <div className="text-xs text-blue-600 font-medium">
                            {vesselCount}v
                          </div>
                        )}
                        {warehouseCount > 0 && (
                          <div className="text-xs text-green-600 font-medium">
                            {warehouseCount}w
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Legend */}
            <div className="mt-3 pt-3 border-t space-y-1.5">
              <div className="text-xs font-medium text-gray-700 mb-1.5">Legend:</div>
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <div className="w-3 h-3 bg-yellow-50 border rounded"></div>
                <span>1-2 vessels</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <div className="w-3 h-3 bg-orange-50 border rounded"></div>
                <span>3-4 vessels</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <div className="w-3 h-3 bg-red-50 border rounded"></div>
                <span>5+ vessels</span>
              </div>
              <div className="mt-2 pt-2 border-t space-y-1">
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-blue-600 font-semibold">Xv</span>
                  <span className="text-gray-600">= Vessel deliveries</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-green-600 font-semibold">Xw</span>
                  <span className="text-gray-600">= Warehouse releases</span>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Right side - Filters */}
        <div className="lg:col-span-2">
          <Card>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Filter size={20} className="text-blue-600" />
                <h3 className="text-lg font-semibold">Load List Filters</h3>
                <span className="text-sm text-gray-500">(Delivery inventory loading from warehouse)</span>
              </div>
              <Button onClick={clearFilters} size="small">
                Clear Filters
              </Button>
            </div>

            {/* Selected Date Display */}
            <div className="mb-4 p-3 bg-blue-50 rounded-lg">
              <div className="text-sm font-medium text-gray-700">Selected Date:</div>
              <div className="text-lg font-semibold text-blue-600">
                {selectedDate.format('dddd, MMMM D, YYYY')}
              </div>
            </div>

            {/* Vessel List */}
            <div className="space-y-2 mb-4">
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                <Truck size={16} />
                <span>Vessels for Selected Date ({vesselsWithServices.length})</span>
              </label>

              {vesselsWithServices.length === 0 ? (
                <div className="p-4 bg-gray-50 rounded-lg text-center text-sm text-gray-500">
                  No vessels have delivery services scheduled for this date
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {vesselsWithServices.map(({ vessel, serviceCount, inventoryCount }) => (
                    <div
                      key={vessel.id}
                      className="flex items-center justify-between p-3 bg-white border rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center space-x-2">
                        <Truck size={14} className="text-gray-400" />
                        <span className="font-medium text-gray-900">{vessel.name}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        {serviceCount > 0 && (
                          <Tag color="blue" size="small">
                            {serviceCount} service{serviceCount !== 1 ? 's' : ''}
                          </Tag>
                        )}
                        {inventoryCount > 0 && (
                          <Tag color="green" size="small">
                            {inventoryCount} pkg{inventoryCount !== 1 ? 's' : ''}
                          </Tag>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Sort Options */}
            <div className="pt-4 border-t">
              <div className="flex items-center justify-between">
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                  <SortAsc size={16} />
                  <span>Sort By</span>
                </label>
                <Select
                  value={filters.sortBy}
                  onChange={setSortBy}
                  className="w-48"
                  options={[
                    { value: SortBy.DATE, label: "Service Date" },
                    { value: SortBy.VESSEL, label: "Vessel Name" },
                    { value: SortBy.WEEK, label: "Week" },
                  ]}
                />
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};
